/**
 * Backend-for-Frontend Authentication
 * All auth is handled by the backend.
 */

import { PLATFORM_API_URL } from "@/shared/constants/projectConfig";

const BACKEND_URL = PLATFORM_API_URL;

/**
 * Event name dispatched on `window` when an authed API call returns 401 from
 * a session-required endpoint. The BFF auth provider listens for this and
 * triggers a clean redirect to /login. Helpers that hit session-required
 * routes should call `notifyUnauthorized()` on 401 so the UI reacts
 * immediately instead of waiting for the periodic session probe.
 */
export const UNAUTHORIZED_EVENT = "auth:unauthorized";

export function notifyUnauthorized(): void {
  if (globalThis.window === undefined) return;
  globalThis.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

export interface UserSummary {
  uuid: string;
  username: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  // Address (free text — `null` means unset)
  country: string | null;
  street: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  // Company
  company_name: string | null;
  job_title: string | null;
  company_size: string | null;
  company_website: string | null;
  membership_role: string;
  is_active: boolean;
}

export interface AccountSummary {
  uuid: string;
  name: string;
  slug: string;
  status: string;
  // Whether the account has a default AI gateway API key, i.e. whether
  // inference can run. `undefined` when talking to a backend that predates the
  // field — check `=== false`, never `!has_inference_key`.
  has_inference_key: boolean;
}

/** One workspace the signed-in user may switch into. */
export interface AccountMembership {
  uuid: string;
  name: string;
  slug: string;
  status: string;
  /** The user's role in *this* workspace — not necessarily their default one. */
  membership_role: string;
}

/**
 * Server response from `/api/auth/me` after the BFF migration.
 * Legacy keys (`sub`, `username`, ...) are kept on the wire for callers that
 * haven't moved off the old shape.
 */
export interface UserInfo {
  user: UserSummary;
  account: AccountSummary | null;
  needs_onboarding: boolean;
  /**
   * Every workspace the user belongs to. Optional so this frontend ships
   * independently of the backend that adds it — read it through
   * `accountMemberships()`, never directly.
   */
  accounts?: AccountMembership[];
  active_account_uuid?: string | null;

  // Legacy / convenience copies
  sub?: string | null;
  username?: string;
  email?: string;
  preferred_username?: string;
  // Older callers expected these — populated for compatibility
  name?: string;
  full_name?: string;
}

/**
 * The workspaces to show in the switcher.
 *
 * Degrades to the single active account when talking to a backend that predates
 * `accounts`, so the switcher renders the same thing it always did rather than
 * an empty list.
 */
export function accountMemberships(info: UserInfo | null): AccountMembership[] {
  if (!info) return [];
  if (info.accounts && info.accounts.length > 0) return info.accounts;
  if (!info.account) return [];
  return [
    {
      uuid: info.account.uuid,
      name: info.account.name,
      slug: info.account.slug,
      status: info.account.status,
      membership_role: info.user.membership_role,
    },
  ];
}

/**
 * Point this session at another workspace. Returns the fresh `/me` payload.
 *
 * The caller is expected to do a **hard navigation** afterwards rather than
 * re-render: account-scoped data is spread across a TanStack cache, plain
 * `useState` fetches, module-level flags and open SSE readers, and a reload is
 * the only way to be sure none of the previous tenant's data survives.
 */
export async function switchAccount(accountUuid: string): Promise<UserInfo> {
  const response = await fetch(`${BACKEND_URL}/api/accounts/switch`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ account_uuid: accountUuid }),
  });
  if (response.status === 401) {
    notifyUnauthorized();
    throw new Error("Your session has expired. Sign in again.");
  }
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "That workspace is no longer available to you."
        : "Could not switch workspace. Try again.",
    );
  }
  return (await response.json()) as UserInfo;
}

export interface SessionStatus {
  authenticated: boolean;
  user_uuid?: string;
  username?: string;
  account_uuid?: string | null;
  needs_onboarding?: boolean;
  sub?: string | null;
}

/**
 * Redirect to backend login. `idpHint` jumps to a brokered IdP (e.g. "google").
 * `returnTo` is a path on the frontend the callback will redirect to after
 * login (must start with "/").
 */
export async function redirectToLogin(opts?: { idpHint?: string; returnTo?: string }) {
  try {
    await fetch(`${BACKEND_URL}/api/auth/clear-oauth-state`, {
      credentials: "include",
    });
  } catch (error) {
    console.warn("Failed to clear OAuth state:", error);
  }

  const params = new URLSearchParams();
  if (opts?.idpHint) params.set("idp_hint", opts.idpHint);
  if (opts?.returnTo?.startsWith("/")) params.set("return_to", opts.returnTo);
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : "";
  globalThis.location.href = `${BACKEND_URL}/api/auth/login${suffix}`;
}

/**
 * The WiLine Workforce desktop app sends people here to sign in, carrying a
 * signed handoff blob in `?native=`. Once they're authenticated we send them
 * back to the backend, which shows a "Return to Workforce" screen and — only on
 * that click — hands the app its credential.
 *
 * The blob grants nothing on its own and is signed by the backend, so the
 * frontend just carries it through; it never inspects or builds one.
 */
export const NATIVE_HANDOFF_PARAM = "native";

/** The handoff blob for the current page, if a desktop login is in flight. */
export function nativeHandoffBlob(): string | null {
  if (typeof globalThis.location === "undefined") return null;
  return new URLSearchParams(globalThis.location.search).get(NATIVE_HANDOFF_PARAM);
}

/** The frontend path that keeps a handoff alive across a federated round-trip. */
export function nativeHandoffReturnTo(blob: string): string {
  return `/login?${NATIVE_HANDOFF_PARAM}=${encodeURIComponent(blob)}`;
}

export function redirectToNativeHandoff(blob: string) {
  const qs = new URLSearchParams({ [NATIVE_HANDOFF_PARAM]: blob }).toString();
  globalThis.location.href = `${BACKEND_URL}/api/auth/native/complete?${qs}`;
}

export async function logout() {
  await fetch(`${BACKEND_URL}/api/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
}

export async function checkSession(): Promise<SessionStatus> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
      credentials: "include",
    });
    if (!response.ok) return { authenticated: false };
    return await response.json();
  } catch (error) {
    console.error("Session check failed:", error);
    return { authenticated: false };
  }
}

export async function getCurrentUser(): Promise<UserInfo | null> {
  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
      credentials: "include",
    });
    if (response.status === 401) {
      // Session is gone server-side — the BFF auth provider listens for
      // this event and clears local state + bounces the user to /login.
      notifyUnauthorized();
      return null;
    }
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error("Get user info failed:", error);
    return null;
  }
}

export interface SignupRequest {
  email: string;
  password: string;
  username: string;
  full_name?: string;
}

export interface SignupResponse {
  success: boolean;
  needs_onboarding: boolean;
  auto_login: boolean;
  email: string;
}

export interface UsernameAvailability {
  available: boolean;
  reason?: "invalid_format" | "taken" | "service_unavailable";
}

/**
 * Check if a username is still available. Designed to be called repeatedly
 * by a debounced effect — pass an `AbortSignal` so older in-flight requests
 * are cancelled when the user keeps typing.
 *
 * Resolves to `{ available: false, reason: "service_unavailable" }` on
 * network or backend errors so the caller can degrade gracefully without
 * blocking submission.
 */
export async function checkUsernameAvailability(
  value: string,
  signal?: AbortSignal,
): Promise<UsernameAvailability> {
  try {
    const response = await fetch(
      `${BACKEND_URL}/api/auth/check-username?value=${encodeURIComponent(value)}`,
      { credentials: "include", signal },
    );
    if (!response.ok) {
      return { available: false, reason: "service_unavailable" };
    }
    return (await response.json()) as UsernameAvailability;
  } catch (error) {
    if ((error as { name?: string })?.name === "AbortError") throw error;
    return { available: false, reason: "service_unavailable" };
  }
}

/**
 * Create a new identity (Keycloak user). On success the backend may have
 * already set a session cookie (auto_login=true) — in which case the caller
 * can navigate straight to /onboarding. Otherwise the caller should redirect
 * the user to redirectToLogin({returnTo: "/onboarding"}).
 *
 * Throws an Error with a user-facing `message` on validation / conflict
 * failures so the caller can render it inline in the form.
 */
export interface InvitationPreview {
  email: string;
  account_name: string;
  role: string;
  expires_at: string;
  /** False when the address already has an identity — they must sign in. */
  can_set_password: boolean;
}

/** Read an invite link. 404 means expired, revoked, spent or never existed. */
export async function getInvitation(
  token: string,
): Promise<InvitationPreview | null> {
  const response = await fetch(
    `${BACKEND_URL}/api/auth/invitation/${encodeURIComponent(token)}`,
  );
  if (!response.ok) return null;
  return (await response.json()) as InvitationPreview;
}

/** Accept by creating the identity the invitation names. The email is not sent:
 *  the token decides it, so a link can only ever create its own address. */
export async function acceptInvitationWithPassword(
  token: string,
  payload: { username: string; password: string; full_name?: string },
): Promise<SignupResponse> {
  const response = await fetch(
    `${BACKEND_URL}/api/auth/invitation/${encodeURIComponent(token)}/accept`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    },
  );

  let body: SignupResponse | { detail?: string } | null = null;
  try {
    body = await response.json();
  } catch {
    /* response had no JSON body */
  }
  if (!response.ok) {
    const detail =
      (body && "detail" in body && body.detail) ||
      `Could not accept the invitation (${response.status})`;
    throw new Error(detail);
  }
  return body as SignupResponse;
}

/**
 * Accept an invitation while already signed in.
 *
 * Adds the account **alongside** whatever workspaces the caller already has and
 * switches this session into it. Refused (403) when the invitation was sent to
 * a different address than the one they are signed in as.
 */
export async function joinInvitation(token: string): Promise<UserInfo> {
  const response = await fetch(
    `${BACKEND_URL}/api/auth/invitation/${encodeURIComponent(token)}/join`,
    { method: "POST", credentials: "include" },
  );

  let body: UserInfo | { detail?: string } | null = null;
  try {
    body = await response.json();
  } catch {
    /* response had no JSON body */
  }
  if (!response.ok) {
    const detail =
      (body && "detail" in body && body.detail) ||
      `Could not join (${response.status})`;
    throw new Error(String(detail));
  }
  return body as UserInfo;
}

export async function signup(payload: SignupRequest): Promise<SignupResponse> {
  const response = await fetch(`${BACKEND_URL}/api/auth/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  let body: SignupResponse | { detail?: string } | null = null;
  try {
    body = await response.json();
  } catch {
    /* response had no JSON body */
  }

  if (!response.ok) {
    const detail =
      (body && "detail" in body && body.detail) ||
      `Sign-up failed (${response.status})`;
    throw new Error(detail);
  }
  return body as SignupResponse;
}

// Email OTP verification (signup steps 1 -> 2)

export interface SendEmailCodeResponse {
  success: boolean;
  email: string;
  /** True when the realm already knows this email but we don't have a local
   * row yet (shared-realm SSO). The caller should send the user to /login
   * instead of through the OTP flow — the local row is auto-provisioned on
   * first sign-in. */
  needs_signin?: boolean;
  reason?: "existing_sso_account";
}

export interface VerifyEmailCodeResponse {
  success: boolean;
  verified: boolean;
  message: string;
  email: string;
}

/**
 * Ask the backend to mail an OTP to `email`. Throws on 4xx/5xx with the
 * backend `detail` as the message so the signup form can show it inline.
 * 409 (account already exists) is the case the form prompts the user to
 * sign in instead.
 */
export async function sendEmailCode(email: string): Promise<SendEmailCodeResponse> {
  const response = await fetch(`${BACKEND_URL}/api/auth/send-email-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email }),
  });
  const body = await parseJsonOrEmpty(response);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      readDetailString(body) || `Could not send code (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
  return body as SendEmailCodeResponse;
}

/**
 * Submit the OTP the user typed. The backend always returns a structured
 * body (even on a wrong code) so callers can render `message` inline
 * without an exception. We still throw on 429 / network failures.
 */
export async function verifyEmailCode(
  email: string,
  otp_code: string,
): Promise<VerifyEmailCodeResponse> {
  const response = await fetch(`${BACKEND_URL}/api/auth/verify-email-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, otp_code }),
  });
  const body = await parseJsonOrEmpty(response);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      readDetailString(body) || `Could not verify code (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
  return body as VerifyEmailCodeResponse;
}

// Password login (direct grant via backend)

export interface LoginPasswordResponse {
  success: boolean;
  needs_onboarding: boolean;
}

export async function loginWithPassword(
  username_or_email: string,
  password: string,
): Promise<LoginPasswordResponse> {
  const response = await fetch(`${BACKEND_URL}/api/auth/login-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ username_or_email, password }),
  });
  const body = await parseJsonOrEmpty(response);
  if (!response.ok) {
    throw new ApiError(
      response.status,
      readDetailString(body) || `Sign-in failed (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
  return body as LoginPasswordResponse;
}

// Profile self-service

export interface UpdateProfilePayload {
  full_name?: string | null;
  username?: string;
  avatar_url?: string | null;
  // Address — pass an empty string to clear; omit to leave unchanged.
  country?: string | null;
  street?: string | null;
  city?: string | null;
  region?: string | null;
  postal_code?: string | null;
  // Company
  company_name?: string | null;
  job_title?: string | null;
  company_size?: string | null;
  company_website?: string | null;
}

/** Generic API error — exposes the structured `detail` payload (e.g. for the
 * `last_owner` 409 case where the backend returns counts the UI renders). */
export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, message: string, detail: unknown) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function parseJsonOrEmpty(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function readDetailString(body: unknown): string | null {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object" && "message" in detail) {
      const msg = (detail as { message: unknown }).message;
      if (typeof msg === "string") return msg;
    }
  }
  return null;
}

export async function updateProfile(
  payload: UpdateProfilePayload,
): Promise<UserInfo> {
  const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const body = await parseJsonOrEmpty(response);
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    throw new ApiError(
      response.status,
      readDetailString(body) || `Profile update failed (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
  return body as UserInfo;
}

export interface UploadAvatarResponse {
  avatar_url: string;
}

export async function uploadAvatar(file: File): Promise<UploadAvatarResponse> {
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${BACKEND_URL}/api/auth/me/avatar`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const body = await parseJsonOrEmpty(response);
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    throw new ApiError(
      response.status,
      readDetailString(body) || `Avatar upload failed (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
  return body as UploadAvatarResponse;
}

export async function deleteAvatar(): Promise<void> {
  const response = await fetch(`${BACKEND_URL}/api/auth/me/avatar`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok && response.status !== 204) {
    const body = await parseJsonOrEmpty(response);
    if (response.status === 401) notifyUnauthorized();
    throw new ApiError(
      response.status,
      readDetailString(body) || `Avatar removal failed (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
}

export interface DeleteAccountResponse {
  email: string;
  local_deleted: boolean;
  keycloak_deleted: boolean | null;
  projects_unowned: number;
  data_sources_unowned: number;
}

export async function deleteMyAccount(): Promise<DeleteAccountResponse> {
  const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
    method: "DELETE",
    credentials: "include",
  });
  const body = await parseJsonOrEmpty(response);
  if (!response.ok) {
    if (response.status === 401) notifyUnauthorized();
    throw new ApiError(
      response.status,
      readDetailString(body) || `Account deletion failed (${response.status})`,
      (body as { detail?: unknown })?.detail ?? null,
    );
  }
  return body as DeleteAccountResponse;
}
